import { useEffect, useRef, useState } from 'react'

import { IconCheck, IconCopy } from '@tabler/icons-react'
import QRCode from 'qrcode'
import { toast } from 'sonner'

import {
  useActivate2Fa,
  useGenerate2FaCode,
} from '@/api/v1/administration/user/2fa'
import { DataLoadError } from '@/components/common/data-load-error'
import { LoadingButton } from '@/components/common/loading-button'
import {
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
} from '@/components/common/responsive-dialog'
import { Button } from '@/components/sui/button'
import { FieldLabel } from '@/components/sui/field'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/sui/input-otp'
import { Skeleton } from '@/components/sui/skeleton'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { logger } from '@/lib/logger'

import locales from './locales'

const TOTP_REGEX = /^\d{6}$/

type TwoFactorSetupProps = {
  onCompleted: () => void
  onActivated?: () => void
  onCancel?: () => void
}

export function TwoFactorSetup({
  onCompleted,
  onActivated,
  onCancel,
}: TwoFactorSetupProps) {
  const { t } = useComponentTranslation(locales)

  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null)
  const [codeLoadFailed, setCodeLoadFailed] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [totpError, setTotpError] = useState<string>()
  const [recoveryCodes, setRecoveryCodes] = useState<Array<string> | null>(null)
  const [codesCopied, setCodesCopied] = useState(false)

  const generate2FaCodeMutation = useGenerate2FaCode()
  const activate2FaMutation = useActivate2Fa({
    successMessage: t('messages.success'),
    errorMessage: t('messages.error'),
  })

  // mutateAsync + local error state, not mutate-with-callbacks: StrictMode's
  // simulated unmount detaches the mutation observer mid-flight, so for a
  // mutation fired from the mount effect, mutate()'s onSuccess/onError — and
  // the hook's isError — never update in dev. The promise returned by
  // mutateAsync comes straight from the mutation and is delivered regardless.
  const generateQrCode = async () => {
    setQrCodeDataUrl(null)
    setCodeLoadFailed(false)
    logger.info('Starting 2FA setup', {}, 'security')
    try {
      const data = await generate2FaCodeMutation.mutateAsync()
      if (!data.code) return
      try {
        const dataUrl = await QRCode.toDataURL(data.code, {
          errorCorrectionLevel: 'H',
          margin: 2,
          width: 256,
        })
        setQrCodeDataUrl(dataUrl)
      } catch (error) {
        logger.error(
          'Failed to generate QR code',
          error instanceof Error ? error : undefined,
          {},
          'security',
        )
      }
    } catch (error) {
      logger.error(
        'Failed to generate 2FA code',
        error instanceof Error ? error : undefined,
        {},
        'security',
      )
      setCodeLoadFailed(true)
    }
  }

  // Each generate call resets the authenticator key server-side, so guard
  // against StrictMode's double effect run to keep the shown QR valid.
  const hasRequestedCodeRef = useRef(false)
  useEffect(() => {
    if (hasRequestedCodeRef.current) return
    hasRequestedCodeRef.current = true
    generateQrCode()
  }, [])

  const handleActivate = (code?: string) => {
    const codeToValidate = typeof code === 'string' ? code : totpCode
    if (!codeToValidate) {
      setTotpError(t('validation.codeRequired'))
      return
    }
    if (!TOTP_REGEX.test(codeToValidate)) {
      setTotpError(t('validation.codeInvalid'))
      return
    }

    logger.info('Activating 2FA', {}, 'security')
    activate2FaMutation.mutate(
      { totp: codeToValidate },
      {
        onSuccess: (data) => {
          logger.info('2FA activated successfully', {}, 'security')
          onActivated?.()
          if (data.recoveryCodes && data.recoveryCodes.length > 0) {
            setRecoveryCodes(data.recoveryCodes)
            return
          }
          onCompleted()
        },
        onError: (error) => {
          logger.error(
            'Failed to activate 2FA',
            error instanceof Error ? error : undefined,
            {},
            'security',
          )
          setTotpCode('')
          setTotpError(t('validation.codeIncorrect'))
        },
      },
    )
  }

  const handleCopyRecoveryCodes = async () => {
    if (!recoveryCodes) return
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      setCodesCopied(true)
      toast.success(t('toast.codesCopied'))
      setTimeout(() => setCodesCopied(false), 2000)
    } catch {
      logger.error('Failed to copy recovery codes', undefined, {}, 'security')
    }
  }

  if (recoveryCodes) {
    return (
      <>
        <ResponsiveDialogBody>
          <div className="space-y-4">
            <p className="text-sm">{t('recoveryCodes.description')}</p>
            <div className="bg-muted grid grid-cols-2 gap-2 rounded-md p-4 font-mono text-sm">
              {recoveryCodes.map((code) => (
                <div key={code} className="text-center">
                  {code}
                </div>
              ))}
            </div>
            <p className="text-muted-foreground text-xs">
              {t('recoveryCodes.warning')}
            </p>
          </div>
        </ResponsiveDialogBody>
        <ResponsiveDialogFooter>
          <Button variant="outline" onClick={handleCopyRecoveryCodes}>
            {codesCopied ? (
              <IconCheck className="mr-2 h-4 w-4 text-green-500" />
            ) : (
              <IconCopy className="mr-2 h-4 w-4" />
            )}
            {t('recoveryCodes.actions.copy')}
          </Button>
          <Button onClick={onCompleted}>
            {t('recoveryCodes.actions.done')}
          </Button>
        </ResponsiveDialogFooter>
      </>
    )
  }

  if (codeLoadFailed) {
    return (
      <ResponsiveDialogBody>
        <DataLoadError message={t('error.load')} onRetry={generateQrCode} />
      </ResponsiveDialogBody>
    )
  }

  return (
    <>
      <ResponsiveDialogBody>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <p className="text-sm">{t('step1')}</p>
            <div className="flex flex-col items-center space-y-4">
              {qrCodeDataUrl ? (
                <img
                  src={qrCodeDataUrl}
                  alt={t('qrCodeAlt')}
                  className="h-48 w-48 rounded-md"
                />
              ) : (
                <Skeleton className="h-48 w-48 rounded-md" />
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm">{t('step2')}</p>
            <FieldLabel htmlFor="totp-code">{t('code.label')}</FieldLabel>
            <InputOTP
              maxLength={6}
              value={totpCode}
              onChange={(value) => {
                setTotpCode(value)
                setTotpError(undefined)
              }}
              onComplete={handleActivate}
              id="totp-code"
              aria-describedby={totpError ? 'totp-code-error' : undefined}
              containerClassName="w-full"
            >
              <InputOTPGroup className="w-full gap-2.5 *:data-[slot=input-otp-slot]:flex-1 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border">
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
            {totpError && (
              <p
                id="totp-code-error"
                role="alert"
                className="text-destructive text-sm"
              >
                {totpError}
              </p>
            )}
          </div>
        </div>
      </ResponsiveDialogBody>
      <ResponsiveDialogFooter>
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={activate2FaMutation.isPending}
          >
            {t('actions.cancel')}
          </Button>
        )}
        <LoadingButton
          onClick={() => handleActivate()}
          isLoading={activate2FaMutation.isPending}
          disabled={!qrCodeDataUrl}
        >
          {t('actions.verify')}
        </LoadingButton>
      </ResponsiveDialogFooter>
    </>
  )
}
